import type { ColumnDef } from '@tanstack/react-table'
import type { Order } from '@/api/resources/orders'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod/v4'
import { ORDER_STATUSES, ordersQueryOptions } from '@/api/resources/orders'
import { DataTable } from '@/components/data-table/data-table'
import { OrganisationFilter } from '@/components/layout/organisation-filter'
import { PageHeader } from '@/components/layout/page-header'
import { OrderStatusBadge } from '@/components/orders/order-status-badge'
import { useOrganisationFilter } from '@/hooks/use-organisation-filter'
import { usePagination } from '@/hooks/use-pagination'
import { formatDate } from '@/lib/format'

const searchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  status: z.enum(ORDER_STATUSES).optional().catch(undefined),
})

export const Route = createFileRoute('/_app/orders/')({
  head: () => ({ meta: [{ title: 'Orders - MyJunea' }] }),
  validateSearch: (search) => searchSchema.parse(search),
  component: OrdersPage,
})

const orderColumns: ColumnDef<Order, unknown>[] = [
  {
    accessorKey: 'created_at',
    header: 'Date',
    cell: ({ row }) => {
      const date = row.original.created_at
      return date !== null ? formatDate(date) : '—'
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <OrderStatusBadge status={row.original.status} label={row.original.status_label} />
    ),
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => <span className="capitalize">{row.original.type}</span>,
  },
  {
    accessorKey: 'organisation_name',
    header: 'Organisation',
    cell: ({ row }) => row.original.organisation_name ?? '—',
  },
  {
    accessorKey: 'location_name',
    header: 'Location',
    cell: ({ row }) => row.original.location_name ?? '—',
  },
  {
    accessorKey: 'items_count',
    header: 'Items',
  },
]

function OrdersPage(): React.ReactElement {
  const { page, status } = Route.useSearch()
  const { setPage } = usePagination(page)
  const organisationFilter = useOrganisationFilter()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery(ordersQueryOptions({ page, status, ...organisationFilter }))

  const handleRowClick = (order: Order): void => {
    void navigate({ to: '/orders/$orderId', params: { orderId: String(order.id) } })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Orders"
        description="Track restocking orders and delivery status."
        filters={<OrganisationFilter />}
      />
      <DataTable
        columns={orderColumns}
        data={data?.data ?? []}
        isLoading={isLoading}
        pagination={
          data
            ? {
                current_page: data.meta.current_page,
                last_page: data.meta.last_page,
                total: data.meta.total,
              }
            : undefined
        }
        onPageChange={setPage}
        onRowClick={handleRowClick}
        emptyMessage="No orders found."
        renderMobileCard={(order) => (
          <div className="bg-card rounded-xl border p-4">
            <div className="flex items-start justify-between">
              <div className="font-medium">
                {order.created_at !== null ? formatDate(order.created_at) : '—'}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm capitalize">{order.type}</span>
                <OrderStatusBadge status={order.status} label={order.status_label} />
              </div>
            </div>
            <div className="text-muted-foreground mt-1 text-sm">
              {order.organisation_name ?? '—'} · {order.location_name ?? '—'}
            </div>
            <div className="text-muted-foreground mt-1 text-sm">
              {order.items_count} {order.items_count === 1 ? 'item' : 'items'}
            </div>
          </div>
        )}
      />
    </div>
  )
}
